/**
 * Right-side sidebar + bottom toolbar — unified L-shaped container.
 *
 * Sidebar is a flex child (the sidebar). ToolbarStrip is a separate
 * absolutely-positioned element rendered inside the viewport div. Both use
 * the same opaque background so they read as one seamless L-shaped unit.
 *
 * The toolbar lives in the viewport div (not inside Sidebar) because flex
 * children clip overflow — the toolbar needs to extend left past the sidebar
 * column. It sits at bottom:0, right:0 in the viewport, which is exactly
 * where the sidebar's left edge begins, so they're flush.
 */

import {
  ChevronDown,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ExternalLink,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { IconButton } from "./Button.tsx";
import {
  ALWAYS_DOCKED_PANELS,
  FLOATING_PANEL_IDS,
  type FloatingPanelId,
  loadDockWidth,
  MANNEQUIN_ONLY_PANELS,
  PANEL_ALLOWED_MODES,
  saveDockWidth,
  useEditorMode,
  useFloatingPanelStore,
} from "./floatingPanelStore.ts";
import { ModeSwitcher } from "./ModeSwitcher.tsx";
import { SidebarControls } from "./modeControlRegistry.tsx";
import {
  PANEL_COLORS,
  PANEL_META,
  PanelBody,
  useMannequinObjectId,
  usePanelApplicabilities,
} from "./panelRegistry.tsx";
import { DirtyIndicator } from "./StatusBar.tsx";
import { GLASS_PANEL } from "./styles.ts";
import {
  COLLAPSE_MS,
  COLOR_BORDER,
  COLOR_HEADER_BG,
  COLOR_TEXT_DISABLED,
  COLOR_TEXT_FAINT,
  COLOR_TEXT_MUTED,
  DOCK_COLLAPSED_WIDTH,
  DOCK_MAX_WIDTH,
  DOCK_MIN_WIDTH,
  FONT_SIZE_PANEL_TITLE,
  FONT_SIZE_TINY,
  RADIUS_HEADER,
  RADIUS_LARGE,
  RADIUS_SM,
  RADIUS_XS,
  useChromeDim,
  useIsMobile,
  Z_TOOLBAR,
} from "./tokens.ts";
import { ToolSurface } from "./tool/ToolSurface.tsx";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Height of the horizontal toolbar arm. */
const TOOLBAR_HEIGHT = DOCK_COLLAPSED_WIDTH;
/** CSS calc expression for toolbar height including safe-area inset. */
const TOOLBAR_HEIGHT_SAFE = `calc(${TOOLBAR_HEIGHT}px + env(safe-area-inset-bottom, 0px) * 0.5)`;

/** Radius for the concave inner corner at the L-junction. */
const CORNER_RADIUS = RADIUS_HEADER;

/**
 * Opaque background shared by both L-shape arms.
 *
 * This is the composite of the old `rgba(22,22,26,0.6)` over the viewport
 * background `#1a1a1e`. Using an opaque value guarantees the sidebar and
 * toolbar always match, regardless of what's behind them (3D scene, etc.).
 */
const L_BG = COLOR_HEADER_BG;

// ---------------------------------------------------------------------------
// Highlight animation for panels revealed from collapsed dock
// ---------------------------------------------------------------------------

const PANEL_HIGHLIGHT_MS = 1200;
const PANEL_HIGHLIGHT_ANIM = "dock-panel-highlight";

let _panelHighlightInjected = false;
function ensurePanelHighlightStyle() {
  if (_panelHighlightInjected) return;
  const style = document.createElement("style");
  style.textContent = `
@keyframes ${PANEL_HIGHLIGHT_ANIM} {
  0%   { box-shadow: inset 0 2px 0 var(--hl-color), inset 3px 0 0 var(--hl-color); }
  50%  { box-shadow: inset 0 2px 0 var(--hl-color), inset 3px 0 0 var(--hl-color); }
  100% { box-shadow: inset 0 0 0 transparent, inset 0 0 0 transparent; }
}`;
  document.head.appendChild(style);
  _panelHighlightInjected = true;
}

// ---------------------------------------------------------------------------
// ToolbarStrip — the horizontal arm of the L-shape.
//
// Rendered inside the viewport div (in App.tsx), absolutely positioned at
// bottom-right so it's flush against the sidebar's left edge.
//
// A concave corner SVG is absolutely positioned just above the toolbar's
// right edge — it draws a quarter-circle of viewport background color to
// carve a smooth concave transition between the toolbar and sidebar.
// ---------------------------------------------------------------------------

export function ToolbarStrip() {
  const chromeDim = useChromeDim();
  return (
    <ToolSurface edge="bottom">
      <div
        style={{
          position: "absolute",
          bottom: 0,
          right: 0,
          height: TOOLBAR_HEIGHT_SAFE,
          zIndex: Z_TOOLBAR,
          pointerEvents: "auto",
          background: L_BG,
          borderTop: `0.5px solid ${COLOR_BORDER}`,
          borderLeft: `0.5px solid ${COLOR_BORDER}`,
          borderTopLeftRadius: CORNER_RADIUS,
          display: "flex",
          alignItems: "center",
          padding: "0 6px",
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) * 0.5)",
          ...chromeDim,
        }}
      >
        <DirtyIndicator />
        <ModeSwitcher />

        {/* Concave inner corner — L_BG wedge bridging toolbar top to sidebar left */}
        <svg
          width={CORNER_RADIUS}
          height={CORNER_RADIUS}
          viewBox={`0 0 ${CORNER_RADIUS} ${CORNER_RADIUS}`}
          style={{
            position: "absolute",
            top: -CORNER_RADIUS,
            right: 0,
            display: "block",
            pointerEvents: "none",
          }}
        >
          {/* Filled wedge: bottom-right corner with concave arc facing viewport */}
          <path
            d={`M${CORNER_RADIUS},0 L${CORNER_RADIUS},${CORNER_RADIUS} L0,${CORNER_RADIUS} A${CORNER_RADIUS},${CORNER_RADIUS} 0 0,0 ${CORNER_RADIUS},0 Z`}
            fill={L_BG}
          />
          {/* Border stroke along the arc only */}
          <path
            d={`M${CORNER_RADIUS},0 A${CORNER_RADIUS},${CORNER_RADIUS} 0 0,1 0,${CORNER_RADIUS}`}
            fill="none"
            stroke={COLOR_BORDER}
            strokeWidth="0.5"
          />
        </svg>
      </div>
    </ToolSurface>
  );
}

// ---------------------------------------------------------------------------
// Sidebar — the vertical arm of the L-shape (plain flex child).
// ---------------------------------------------------------------------------

export function Sidebar() {
  const mode = useEditorMode();
  const dockedPanels = useFloatingPanelStore((s) => s.dockedPanels);
  const dockPanel = useFloatingPanelStore((s) => s.dockPanel);
  const dockCollapsed = useFloatingPanelStore((s) => s.dockCollapsed);
  const dockPanelCollapsed = useFloatingPanelStore((s) => s.dockPanelCollapsed);
  const toggleDockCollapsed = useFloatingPanelStore(
    (s) => s.toggleDockCollapsed,
  );
  const toggleDockPanelCollapsed = useFloatingPanelStore(
    (s) => s.toggleDockPanelCollapsed,
  );
  const undockPanel = useFloatingPanelStore((s) => s.undockPanel);
  const temporaryPanel = useFloatingPanelStore((s) => s.temporaryPanel);
  const temporaryPanelPinned = useFloatingPanelStore(
    (s) => s.temporaryPanelPinned,
  );
  const setTemporaryPanel = useFloatingPanelStore((s) => s.setTemporaryPanel);

  const mannequinObjectId = useMannequinObjectId();
  const panelApplicable = usePanelApplicabilities();
  const isMobile = useIsMobile();
  const toggleTop = isMobile ? TOGGLE_TOP_MOBILE : TOGGLE_TOP;
  const chromeDim = useChromeDim();

  const [width, setWidth] = useState(loadDockWidth);
  const [highlightedPanel, setHighlightedPanel] =
    useState<FloatingPanelId | null>(null);
  const [exitingPanel, setExitingPanel] = useState<FloatingPanelId | null>(
    null,
  );
  const dragging = useRef(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const panelRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const iconRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const hoverEnterTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverLeaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (hoverEnterTimer.current) clearTimeout(hoverEnterTimer.current);
      if (hoverLeaveTimer.current) clearTimeout(hoverLeaveTimer.current);
      if (exitTimer.current) clearTimeout(exitTimer.current);
    };
  }, []);

  // Handle exit animation when temporaryPanel is cleared
  const lastTemporaryPanel = useRef<FloatingPanelId | null>(null);
  useEffect(() => {
    if (temporaryPanel === null && lastTemporaryPanel.current !== null) {
      const id = lastTemporaryPanel.current;
      setExitingPanel(id);
      if (exitTimer.current) clearTimeout(exitTimer.current);
      exitTimer.current = setTimeout(() => {
        setExitingPanel((cur) => (cur === id ? null : cur));
        exitTimer.current = null;
      }, TEMP_PANEL_APPEAR_MS);
    } else if (temporaryPanel !== null) {
      setExitingPanel(null);
      if (exitTimer.current) {
        clearTimeout(exitTimer.current);
        exitTimer.current = null;
      }
    }
    lastTemporaryPanel.current = temporaryPanel;
  }, [temporaryPanel]);

  const clearTimers = useCallback(() => {
    if (hoverEnterTimer.current) {
      clearTimeout(hoverEnterTimer.current);
      hoverEnterTimer.current = null;
    }
    if (hoverLeaveTimer.current) {
      clearTimeout(hoverLeaveTimer.current);
      hoverLeaveTimer.current = null;
    }
  }, []);

  const handleIconMouseEnter = useCallback(
    (id: FloatingPanelId) => {
      if (hoverLeaveTimer.current) {
        clearTimeout(hoverLeaveTimer.current);
        hoverLeaveTimer.current = null;
      }
      if (temporaryPanel === id) return;

      if (temporaryPanel !== null && !temporaryPanelPinned) {
        // Switching panels — immediate if already open transiently
        setTemporaryPanel(id, false);
      } else if (temporaryPanel === null) {
        // Opening first panel — delay to avoid flicker
        hoverEnterTimer.current = setTimeout(() => {
          setTemporaryPanel(id, false);
          hoverEnterTimer.current = null;
        }, 100);
      }
    },
    [temporaryPanel, temporaryPanelPinned, setTemporaryPanel],
  );

  const handleIconMouseLeave = useCallback(() => {
    if (hoverEnterTimer.current) {
      clearTimeout(hoverEnterTimer.current);
      hoverEnterTimer.current = null;
    }
    if (temporaryPanel && !temporaryPanelPinned) {
      hoverLeaveTimer.current = setTimeout(() => {
        setTemporaryPanel(null);
        hoverLeaveTimer.current = null;
      }, 300);
    }
  }, [temporaryPanel, temporaryPanelPinned, setTemporaryPanel]);

  const handleIconClick = useCallback(
    (id: FloatingPanelId) => {
      clearTimers();
      setTemporaryPanel(id, true);
    },
    [clearTimers, setTemporaryPanel],
  );

  const handlePanelMouseEnter = useCallback(() => {
    if (hoverLeaveTimer.current) {
      clearTimeout(hoverLeaveTimer.current);
      hoverLeaveTimer.current = null;
    }
  }, []);

  const handlePanelMouseLeave = useCallback(() => {
    if (temporaryPanel && !temporaryPanelPinned) {
      hoverLeaveTimer.current = setTimeout(() => {
        setTemporaryPanel(null);
        hoverLeaveTimer.current = null;
      }, 300);
    }
  }, [temporaryPanel, temporaryPanelPinned, setTemporaryPanel]);

  // Close the temporary panel popover if the selection changes and makes it inapplicable.
  useEffect(() => {
    if (temporaryPanel !== null && !panelApplicable[temporaryPanel]) {
      setTemporaryPanel(null);
    }
  }, [temporaryPanel, panelApplicable, setTemporaryPanel]);

  const visiblePanels = (() => {
    const ordered = FLOATING_PANEL_IDS.filter(
      (id) =>
        PANEL_ALLOWED_MODES[id].includes(mode) &&
        (!MANNEQUIN_ONLY_PANELS.has(id) || mannequinObjectId !== null) &&
        panelApplicable[id],
    );
    const pinned = ordered.filter((id) => ALWAYS_DOCKED_PANELS.has(id));
    const rest = ordered.filter((id) => !ALWAYS_DOCKED_PANELS.has(id));
    return [...pinned, ...rest];
  })();

  const effectiveWidth = dockCollapsed ? DOCK_COLLAPSED_WIDTH : width;

  const onResizePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      dragging.current = true;
      const startX = e.clientX;
      const startW = width;

      const onMove = (ev: PointerEvent) => {
        const delta = startX - ev.clientX;
        const next = Math.min(
          DOCK_MAX_WIDTH,
          Math.max(DOCK_MIN_WIDTH, startW + delta),
        );
        setWidth(next);
        window.dispatchEvent(
          new CustomEvent("poseref-dock-resize", { detail: next }),
        );
      };
      const onUp = () => {
        dragging.current = false;
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        setWidth((w) => {
          saveDockWidth(w);
          return w;
        });
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [width],
  );

  const expandAndScrollTo = useCallback(
    (id: FloatingPanelId) => {
      if (dockCollapsed) toggleDockCollapsed();
      if (dockPanelCollapsed.has(id)) toggleDockPanelCollapsed(id);
      setHighlightedPanel(id);
      setTimeout(
        () => setHighlightedPanel((cur) => (cur === id ? null : cur)),
        PANEL_HIGHLIGHT_MS,
      );
      requestAnimationFrame(() => {
        const el = panelRefs.current.get(id);
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    },
    [
      dockCollapsed,
      toggleDockCollapsed,
      dockPanelCollapsed,
      toggleDockPanelCollapsed,
    ],
  );

  // ---------------------------------------------------------------------------
  // Collapsed — icon strip
  // ---------------------------------------------------------------------------
  if (dockCollapsed) {
    return (
      <div
        style={{
          width: DOCK_COLLAPSED_WIDTH,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          paddingTop: 8,
          paddingBottom: `calc(${TOOLBAR_HEIGHT + 4}px + env(safe-area-inset-bottom, 0px) * 0.5)`,
          gap: 4,
          background: L_BG,
          position: "relative",
          zIndex: Z_TOOLBAR,
          ...chromeDim,
        }}
      >
        {/* Left border — split around toggle button, stops above the toolbar */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            height: toggleTop,
            width: 0,
            borderLeft: `0.5px solid ${COLOR_BORDER}`,
          }}
        />
        <div
          style={{
            position: "absolute",
            top: toggleTop + TOGGLE_SIZE,
            left: 0,
            bottom: `calc(${TOOLBAR_HEIGHT + CORNER_RADIUS}px + env(safe-area-inset-bottom, 0px) * 0.5)`,
            width: 0,
            borderLeft: `0.5px solid ${COLOR_BORDER}`,
          }}
        />

        {/* Expand/collapse toggle — attached to left edge */}
        <DockToggleButton
          collapsed={dockCollapsed}
          onClick={toggleDockCollapsed}
          top={toggleTop}
        />

        {/* Panel icons — scrollable */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 4,
          }}
        >
          {visiblePanels.map((id) => {
            const meta = PANEL_META[id];
            const colors = PANEL_COLORS[id];
            const isDocked = dockedPanels.has(id);
            const isActive = temporaryPanel === id;
            return (
              <IconButton
                key={id}
                ref={(el) => {
                  if (el) iconRefs.current.set(id, el);
                  else iconRefs.current.delete(id);
                }}
                onClick={() => {
                  if (isDocked) handleIconClick(id);
                  else dockPanel(id);
                }}
                onMouseEnter={() => {
                  if (isDocked) handleIconMouseEnter(id);
                }}
                onMouseLeave={() => {
                  if (isDocked) handleIconMouseLeave();
                }}
                title={isDocked ? meta.label : `Redock ${meta.label}`}
                label={isDocked ? meta.label : `Redock ${meta.label}`}
                icon={
                  <>
                    {meta.icon}
                    {meta.Badge && <meta.Badge />}
                  </>
                }
                size={32}
                radius={RADIUS_SM}
                style={{
                  background: isActive ? colors.bgIdle : "none",
                  color: colors.fg,
                  opacity: isDocked ? (isActive ? 1 : 0.8) : 0.42,
                  flexShrink: 0,
                  position: "relative",
                }}
              />
            );
          })}
        </div>

        {/* Controls — scrollable, pinned at bottom */}
        <div
          style={{
            flexShrink: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            maxHeight: "50vh",
          }}
        >
          <SidebarControls />
        </div>

        {/* Temporary panel — popover adjacent to the active icon */}
        {(temporaryPanel || exitingPanel) && (
          <TemporaryPanel
            id={temporaryPanel || (exitingPanel as FloatingPanelId)}
            isExiting={!!exitingPanel}
            mode={mode}
            anchorRef={iconRefs}
            onDismiss={() => setTemporaryPanel(null)}
            onExpand={() => {
              setTemporaryPanel(null);
              expandAndScrollTo(
                temporaryPanel || (exitingPanel as FloatingPanelId),
              );
            }}
            onUndock={
              ALWAYS_DOCKED_PANELS.has(
                temporaryPanel || (exitingPanel as FloatingPanelId),
              )
                ? undefined
                : () => {
                    setTemporaryPanel(null);
                    undockPanel(
                      temporaryPanel || (exitingPanel as FloatingPanelId),
                    );
                  }
            }
            onMouseEnter={handlePanelMouseEnter}
            onMouseLeave={handlePanelMouseLeave}
            sidebarWidth={DOCK_COLLAPSED_WIDTH}
            dockWidth={width}
          />
        )}
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Expanded — full panel stack
  // ---------------------------------------------------------------------------
  return (
    <div
      style={{
        width: effectiveWidth,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        background: L_BG,
        position: "relative",
        zIndex: Z_TOOLBAR,
        ...chromeDim,
      }}
    >
      {/* Left border — split around toggle button, stops above the toolbar */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          height: toggleTop,
          width: 0,
          borderLeft: `0.5px solid ${COLOR_BORDER}`,
        }}
      />
      <div
        style={{
          position: "absolute",
          top: toggleTop + TOGGLE_SIZE,
          left: 0,
          bottom: TOOLBAR_HEIGHT_SAFE,
          width: 0,
          borderLeft: `0.5px solid ${COLOR_BORDER}`,
        }}
      />

      {/* Expand/collapse toggle — attached to left edge */}
      <DockToggleButton
        collapsed={dockCollapsed}
        onClick={toggleDockCollapsed}
        top={toggleTop}
      />

      {/* Scrollable panel stack */}
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
          padding: `0 0 calc(8px + env(safe-area-inset-bottom, 0px) * 0.5)`,
        }}
      >
        {visiblePanels.length === 0 && (
          <div
            style={{
              padding: "20px 12px",
              fontSize: FONT_SIZE_TINY,
              color: COLOR_TEXT_DISABLED,
              textAlign: "center",
            }}
          >
            No panels in this mode
          </div>
        )}

        {visiblePanels.map((id) => {
          const isAlwaysDocked = ALWAYS_DOCKED_PANELS.has(id);
          const isDocked = dockedPanels.has(id);
          return (
            <DockedPanel
              key={id}
              id={id}
              mode={mode}
              docked={isDocked}
              collapsed={!isDocked || dockPanelCollapsed.has(id)}
              highlight={highlightedPanel === id}
              onToggleCollapse={() => toggleDockPanelCollapsed(id)}
              onDock={() => dockPanel(id)}
              onUndock={isAlwaysDocked ? undefined : () => undockPanel(id)}
              refCallback={(el) => {
                if (el) panelRefs.current.set(id, el);
                else panelRefs.current.delete(id);
              }}
            />
          );
        })}
      </div>

      {/* Sidebar controls — persistent + mode-specific (footer) */}
      <SidebarControls />

      {/* Resize handle — left edge */}
      <div
        onPointerDown={onResizePointerDown}
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: -4,
          width: 8,
          cursor: "ew-resize",
          zIndex: 1,
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// TemporaryPanel — popover shown adjacent to an icon when dock is collapsed
// ---------------------------------------------------------------------------

const TEMP_PANEL_APPEAR_MS = 150;

function TemporaryPanel({
  id,
  isExiting,
  mode,
  anchorRef,
  onDismiss,
  onExpand,
  onUndock,
  onMouseEnter,
  onMouseLeave,
  sidebarWidth,
  dockWidth,
}: {
  id: FloatingPanelId;
  isExiting?: boolean;
  mode: string;
  anchorRef: React.RefObject<Map<string, HTMLButtonElement>>;
  onDismiss: () => void;
  onExpand: () => void;
  onUndock?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  sidebarWidth: number;
  dockWidth: number;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const colors = PANEL_COLORS[id];
  const meta = PANEL_META[id];
  const panelWidth = Math.max(dockWidth, DOCK_MIN_WIDTH);
  // TemporaryPanel is a floating overlay (the panel is popped out of the
  // collapsed dock), so it participates in the mobile slider-drag dim.
  // DockedPanel (rendered in the expanded sidebar) does not.
  const chromeDim = useChromeDim();

  // Compute anchor position from icon button rect
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);

  useEffect(() => {
    const iconEl = anchorRef.current?.get(id);
    if (!iconEl) return;
    const rect = iconEl.getBoundingClientRect();
    const iconCenterY = rect.top + rect.height / 2;
    // Position panel so it's vertically centred on the icon, clamped to viewport
    const estimatedHeight = 300; // initial estimate; will re-clamp on measure
    let top = iconCenterY - estimatedHeight / 2;
    top = Math.max(8, Math.min(window.innerHeight - estimatedHeight - 8, top));
    setPos({ top, right: sidebarWidth });
  }, [id, anchorRef, sidebarWidth]);

  // Re-clamp once we know actual panel height
  useEffect(() => {
    if (!pos || !panelRef.current) return;
    const el = panelRef.current;
    const h = el.offsetHeight;
    const iconEl = anchorRef.current?.get(id);
    if (!iconEl) return;
    const rect = iconEl.getBoundingClientRect();
    const iconCenterY = rect.top + rect.height / 2;
    let top = iconCenterY - h / 2;
    top = Math.max(8, Math.min(window.innerHeight - h - 8, top));
    if (Math.abs(top - pos.top) > 1) setPos({ ...pos, top });
  });

  // Dismiss on outside click — but not when interacting with child popovers
  // (which render via createPortal to document.body with data-floating-portal)
  useEffect(() => {
    if (isExiting) return;
    const handler = (e: PointerEvent) => {
      if (!panelRef.current) return;
      const target = e.target as HTMLElement;
      // Don't dismiss if clicking inside the panel
      if (panelRef.current.contains(target)) return;
      // Don't dismiss if clicking the icon button itself (toggle handles that)
      const iconEl = anchorRef.current?.get(id);
      if (iconEl?.contains(target)) return;
      // Don't dismiss if clicking inside a portal-rendered popover
      if (target.closest?.("[data-floating-portal]")) return;
      onDismiss();
    };
    window.addEventListener("pointerdown", handler, true);
    return () => window.removeEventListener("pointerdown", handler, true);
  }, [onDismiss, id, anchorRef, isExiting]);

  // Dismiss on Escape
  useEffect(() => {
    if (isExiting) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onDismiss();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onDismiss, isExiting]);

  if (!pos) return null;

  return (
    <div
      ref={panelRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        position: "fixed",
        top: pos.top,
        right: pos.right,
        width: panelWidth,
        zIndex: Z_TOOLBAR + 1,
        borderRadius: RADIUS_LARGE,
        pointerEvents: isExiting ? "none" : "auto",
        ...chromeDim,
      }}
    >
      {/* Inner wrapper owns the enter/exit animation so `animation-fill-mode:
          forwards` doesn't pin `opacity: 1` on the root and clobber chromeDim. */}
      <div
        style={{
          borderRadius: RADIUS_LARGE,
          overflow: "hidden",
          animation: `${isExiting ? "temp-panel-disappear" : "temp-panel-appear"} ${TEMP_PANEL_APPEAR_MS}ms ease-out forwards`,
        }}
      >
        {/* Header */}
        <div
          style={{
            background: GLASS_PANEL.background,
            backdropFilter: GLASS_PANEL.backdropFilter as string,
            WebkitBackdropFilter: GLASS_PANEL.backdropFilter as string,
            border: `0.5px solid ${COLOR_BORDER}`,
            borderBottom: "none",
            borderTopLeftRadius: RADIUS_LARGE,
            borderTopRightRadius: RADIUS_LARGE,
            padding: "8px 12px",
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: FONT_SIZE_PANEL_TITLE,
            fontWeight: 500,
            color: COLOR_TEXT_MUTED,
          }}
        >
          <span
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 20,
              height: 20,
              borderRadius: RADIUS_XS,
              background: colors.bgIdle,
              color: colors.fg,
              flexShrink: 0,
            }}
          >
            {meta.icon}
          </span>
          <span
            style={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {meta.label}
          </span>

          {/* Expand into dock */}
          <IconButton
            onClick={onExpand}
            title="Expand dock to this panel"
            label="Expand dock to this panel"
            icon={<ChevronsLeft size={12} />}
            size={20}
            radius={RADIUS_XS}
            style={{ color: COLOR_TEXT_FAINT }}
          />

          {/* Undock to floating panel */}
          {onUndock && (
            <IconButton
              onClick={onUndock}
              title="Undock to floating panel"
              label="Undock to floating panel"
              icon={<ExternalLink size={12} />}
              size={20}
              radius={RADIUS_XS}
              style={{ color: COLOR_TEXT_FAINT }}
            />
          )}
        </div>

        {/* Body */}
        <div
          style={{
            background: GLASS_PANEL.background,
            backdropFilter: GLASS_PANEL.backdropFilter as string,
            WebkitBackdropFilter: GLASS_PANEL.backdropFilter as string,
            border: `0.5px solid ${COLOR_BORDER}`,
            borderTop: "none",
            borderBottomLeftRadius: RADIUS_LARGE,
            borderBottomRightRadius: RADIUS_LARGE,
            padding: "10px 12px",
            maxHeight: "70vh",
            overflowY: "auto",
          }}
        >
          <PanelBody panelId={id} mode={mode} />
        </div>
      </div>
    </div>
  );
}

// Inject the temporary-panel entrance animation once
let _tempPanelAnimInjected = false;
function ensureTempPanelAnim() {
  if (_tempPanelAnimInjected) return;
  const style = document.createElement("style");
  style.textContent = `
@keyframes temp-panel-appear {
  from { opacity: 0; transform: translateX(8px); }
  to   { opacity: 1; transform: translateX(0); }
}
@keyframes temp-panel-disappear {
  from { opacity: 1; transform: translateX(0); }
  to   { opacity: 0; transform: translateX(8px); }
}
`;
  document.head.appendChild(style);
  _tempPanelAnimInjected = true;
}
ensureTempPanelAnim();

// ---------------------------------------------------------------------------
// DockToggleButton — unified expand/collapse control on the left edge
// ---------------------------------------------------------------------------

const TOGGLE_SIZE = 24;
const TOGGLE_TOP = 12;
/** On mobile the header bar covers the default toggle position. */
const TOGGLE_TOP_MOBILE = 52;

function DockToggleButton({
  collapsed,
  onClick,
  top,
}: {
  collapsed: boolean;
  onClick: () => void;
  top: number;
}) {
  return (
    <IconButton
      onClick={onClick}
      title={collapsed ? "Expand dock (/)" : "Collapse dock (/)"}
      label={collapsed ? "Expand dock (/)" : "Collapse dock (/)"}
      icon={
        collapsed ? <ChevronsLeft size={14} /> : <ChevronsRight size={14} />
      }
      size={TOGGLE_SIZE}
      style={{
        position: "absolute",
        left: -TOGGLE_SIZE,
        top,
        borderTopLeftRadius: RADIUS_SM,
        borderBottomLeftRadius: RADIUS_SM,
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        borderTop: `0.5px solid ${COLOR_BORDER}`,
        borderLeft: `0.5px solid ${COLOR_BORDER}`,
        borderBottom: `0.5px solid ${COLOR_BORDER}`,
        borderRight: "none",
        background: L_BG,
        color: COLOR_TEXT_FAINT,
        zIndex: 2,
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Individual docked panel — colored header + collapsible body
// ---------------------------------------------------------------------------

function DockedPanel({
  id,
  mode,
  docked,
  collapsed,
  highlight,
  onToggleCollapse,
  onDock,
  onUndock,
  refCallback,
}: {
  id: FloatingPanelId;
  mode: string;
  docked: boolean;
  collapsed: boolean;
  highlight?: boolean;
  onToggleCollapse: () => void;
  onDock: () => void;
  onUndock?: () => void;
  refCallback: (el: HTMLDivElement | null) => void;
}) {
  if (highlight) ensurePanelHighlightStyle();

  const meta = PANEL_META[id];
  const colors = PANEL_COLORS[id];
  const headerOpacity = docked ? 1 : 0.52;

  return (
    <div
      ref={refCallback}
      style={{
        flexShrink: 0,
        overflow: "hidden",
        borderTop: `0.5px solid ${COLOR_BORDER}`,
        ...(highlight
          ? ({
              "--hl-color": colors.fg,
              animation: `${PANEL_HIGHLIGHT_ANIM} ${PANEL_HIGHLIGHT_MS}ms ease-out`,
            } as React.CSSProperties)
          : undefined),
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "10px 12px",
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: FONT_SIZE_PANEL_TITLE,
          fontWeight: 500,
          color: COLOR_TEXT_MUTED,
          cursor: "pointer",
          userSelect: "none",
          opacity: headerOpacity,
        }}
        onClick={docked ? onToggleCollapse : onDock}
      >
        <span
          style={{
            display: "flex",
            alignItems: "center",
            color: COLOR_TEXT_FAINT,
            flexShrink: 0,
          }}
        >
          {collapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
        </span>
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 20,
            height: 20,
            borderRadius: RADIUS_XS,
            background: colors.bgIdle,
            color: colors.fg,
            flexShrink: 0,
          }}
        >
          {meta.icon}
        </span>
        <span
          style={{
            flex: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {meta.label}
        </span>

        {docked && onUndock && (
          <IconButton
            data-no-drag
            onClick={(e) => {
              e.stopPropagation();
              onUndock();
            }}
            title="Undock to floating panel"
            label="Undock to floating panel"
            icon={<ExternalLink size={12} />}
            size={20}
            radius={RADIUS_XS}
            style={{ color: COLOR_TEXT_FAINT }}
          />
        )}
      </div>

      {/* Body — accordion collapse */}
      <div
        style={{
          display: "grid",
          gridTemplateRows: collapsed ? "0fr" : "1fr",
          overflow: "hidden",
          transition: `grid-template-rows ${COLLAPSE_MS}ms ease`,
        }}
      >
        <div style={{ overflow: "hidden", minHeight: 0 }}>
          <div
            style={{
              padding: "10px 12px 14px",
            }}
          >
            <PanelBody panelId={id} mode={mode} />
          </div>
        </div>
      </div>
    </div>
  );
}
